import VoiceCallService from '../../api/voiceCallService';
import * as types from '../mutation-types';

const state = {
  activeCalls: [],
  currentCall: null,
  uiFlags: {
    isCalling: false,
    showVoiceCallModal: false,
    isBusy: false,
    isConfirmed: false,
  },
  voiceService: null,
  userAvailability: 'avaiable',
};

export const getters = {
  getActiveCalls: $state => $state.activeCalls,
  getCurrentCall: $state => $state.currentCall,
  getIsCallingStatus: $state => $state.uiFlags.isCalling,
  showVoiceCallModal: $state => $state.uiFlags.showVoiceCallModal,
  getVoiceService: $state => $state.voiceService,
  getIsBusy: $state => $state.uiFlags.isBusy,
  getIsConfirmed: $state => $state.uiFlags.isConfirmed,
};

export const actions = {
  async initialize({ commit, dispatch }, userConfig) {
    const voiceServiceInstance = new VoiceCallService();

    voiceServiceInstance.onIncomingCall = callData => {
      dispatch('handleIncomingCall', callData);
    };

    voiceServiceInstance.onCallEnded = callId => {
      dispatch('handleCallEnded', callId);
    };

    voiceServiceInstance.onCallStarted = callData => {
      dispatch('handleCallStarted', callData);
    };

    voiceServiceInstance.onCallFailed = callId => {
      dispatch('handleCallEnded', callId);
    };

    voiceServiceInstance.onCallConfirmed = () => {
      dispatch('handleCallConfirmed');
    };

    await voiceServiceInstance.initialize(userConfig);
    commit(types.default.SET_VOICE_SERVICE, voiceServiceInstance);
    commit(types.default.SET_USER_AVAILABILITY, userConfig.userAvailability);

    return voiceServiceInstance;
  },

  handleIncomingCall(context, callData) {
    const { commit, state: moduleState } = context;
    if (state.uiFlags.isBusy) {
      moduleState.voiceService.rejectBusy(callData.session);
      return;
    }
    if (
      moduleState.userAvailability === 'busy' ||
      moduleState.userAvailability === 'offline'
    ) {
      moduleState.voiceService.rejectBusy(callData.session);
      return;
    }
    commit(types.default.ADD_ACTIVE_CALL, callData);
    commit(types.default.SET_CURRENT_CALL, callData);
    commit(types.default.SET_CALLING_STATUS, true);
    commit(types.default.SET_VOICE_CALL_MODAL_STATUS, true);
    commit(types.default.SET_BUSY_STATUS, true);
  },

  handleCallStarted({ commit }, callData) {
    commit(types.default.ADD_ACTIVE_CALL, callData);
    commit(types.default.SET_CURRENT_CALL, callData);
    commit(types.default.SET_CALLING_STATUS, true);
    commit(types.default.SET_BUSY_STATUS, true);
  },

  handleCallConfirmed({ commit }) {
    commit(types.default.SET_CONFIRMED_STATUS, true);
  },

  handleCallEnded(context, callId) {
    const { state: moduleState, commit } = context;
    commit(types.default.REMOVE_ACTIVE_CALL, callId);

    if (moduleState.currentCall?.id === callId) {
      commit(types.default.CLEAR_CURRENT_CALL);
      commit(types.default.SET_CALLING_STATUS, false);
      commit(types.default.SET_VOICE_CALL_MODAL_STATUS, false);
      commit(types.default.SET_BUSY_STATUS, false);
      commit(types.default.SET_CONFIRMED_STATUS, false);
    }
  },

  setUserAvailability({ commit }, availability) {
    commit(types.default.SET_USER_AVAILABILITY, availability);
  },

  async makeCall(context, number) {
    const { state: moduleState, commit } = context;
    if (!moduleState.voiceService) {
      throw new Error('Voice service not initialized');
    }

    if (state.uiFlags.isBusy) {
      return;
    }

    try {
      commit(types.default.SET_CALLING_STATUS, true);
      commit(types.default.SET_VOICE_CALL_MODAL_STATUS, true);
      commit(types.default.SET_BUSY_STATUS, true);
      await state.voiceService.makeCall(number);
    } catch (error) {
      commit(types.default.SET_CALLING_STATUS, false);
      commit(types.default.SET_BUSY_STATUS, false);
      throw error;
    }
  },

  acceptIncomingCall(context) {
    const { state: moduleState } = context;
    if (!moduleState.currentCall || !moduleState.voiceService) {
      return;
    }

    moduleState.voiceService.acceptCall(moduleState.currentCall.session);
  },

  rejectIncomingCall(context) {
    const { state: moduleState } = context;
    if (!moduleState.currentCall || !moduleState.voiceService) {
      return;
    }

    moduleState.voiceService.rejectCall(moduleState.currentCall.session);
  },

  endCall(context) {
    const { state: moduleState } = context;
    if (!moduleState.currentCall || !moduleState.voiceService) {
      return;
    }

    moduleState.voiceService.endCall(moduleState.currentCall.session);
  },

  closeVoiceCallModal({ commit }) {
    commit(types.default.SET_VOICE_CALL_MODAL_STATUS, false);
  },
};

export const mutations = {
  [types.default.SET_VOICE_SERVICE]($state, service) {
    $state.voiceService = service;
  },

  [types.default.ADD_ACTIVE_CALL]($state, call) {
    $state.activeCalls.push(call);
  },

  [types.default.REMOVE_ACTIVE_CALL]($state, callId) {
    $state.activeCalls = $state.activeCalls.filter(call => call.id !== callId);
  },

  [types.default.SET_CURRENT_CALL]($state, call) {
    $state.currentCall = call;
  },

  [types.default.CLEAR_CURRENT_CALL]($state) {
    $state.currentCall = null;
  },

  [types.default.SET_CALLING_STATUS]($state, status) {
    $state.uiFlags.isCalling = status;
  },

  [types.default.SET_VOICE_CALL_MODAL_STATUS]($state, status) {
    $state.uiFlags.showVoiceCallModal = status;
  },

  [types.default.SET_BUSY_STATUS]($state, status) {
    $state.uiFlags.isBusy = status;
  },

  [types.default.SET_CONFIRMED_STATUS]($state, status) {
    $state.uiFlags.isConfirmed = status;
  },

  [types.default.SET_USER_AVAILABILITY]($state, availability) {
    $state.userAvailability = availability;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
